import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { 
  FileCheck, Clock, AlertTriangle, CheckCircle, XCircle, Users, Mail,
  Eye, Send, RefreshCw, Star, Building2, User, Phone, Calendar,
  ChevronRight, Edit, Trash2, Plus, Brain, Target, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';

const STATUS_COLORS = {
  "Pendente Aprovação": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "Em Revisão": "bg-blue-100 text-blue-800 border-blue-300",
  "Aprovada": "bg-green-100 text-green-800 border-green-300",
  "Aprovada com Modificações": "bg-green-100 text-green-800 border-green-300",
  "Rejeitada": "bg-red-100 text-red-800 border-red-300",
  "Cancelada": "bg-gray-100 text-gray-800 border-gray-300",
  "Expirada": "bg-orange-100 text-orange-800 border-orange-300"
};

const ESTRATEGIA_COLORS = {
  "CONTACTO_DIRETO": "bg-blue-500",
  "CAMPANHA_URGENTE": "bg-orange-500"
};

export default function PropostasContactoPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [propostas, setPropostas] = useState([]);
  const [propostaSelecionada, setPropostaSelecionada] = useState(null);
  const [activeTab, setActiveTab] = useState('pendentes');
  
  // Modal aprovação
  const [modalAprovar, setModalAprovar] = useState(false);
  const [decisao, setDecisao] = useState({
    acao: 'APROVAR',
    enviar_imediatamente: true,
    observacoes: '',
    motivo_rejeicao: ''
  });

  useEffect(() => {
    carregarPropostas();
  }, [activeTab]);

  const carregarPropostas = async () => {
    try {
      setLoading(true);
      let endpoint = '/propostas-doadores';
      if (activeTab === 'pendentes') {
        endpoint = '/propostas-doadores/pendentes';
      }
      
      const res = await apiClient.get(endpoint);
      setPropostas(res.data.propostas || []);
    } catch (error) {
      toast.error('Erro ao carregar propostas');
    } finally {
      setLoading(false);
    }
  };

  const carregarDetalhesProposta = async (propostaId) => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/propostas-doadores/${propostaId}`);
      setPropostaSelecionada(res.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirAprovacao = (proposta) => {
    carregarDetalhesProposta(proposta.id);
    setDecisao({
      acao: 'APROVAR',
      enviar_imediatamente: true,
      observacoes: '',
      motivo_rejeicao: ''
    });
    setModalAprovar(true);
  };

  const handleAprovarRejeitar = async () => {
    if (!propostaSelecionada) return;
    
    try {
      setLoading(true);
      
      const fd = new FormData();
      fd.append('acao', decisao.acao);
      fd.append('enviar_imediatamente', decisao.enviar_imediatamente);
      if (decisao.observacoes) fd.append('observacoes', decisao.observacoes);
      if (decisao.motivo_rejeicao) fd.append('motivo_rejeicao', decisao.motivo_rejeicao);
      
      await apiClient.post(`/propostas-doadores/${propostaSelecionada.id}/aprovar`, fd);
      
      if (decisao.acao === 'APROVAR') {
        toast.success('Proposta aprovada! Emails enviados aos doadores.');
      } else {
        toast.success('Proposta rejeitada.');
      }
      
      setModalAprovar(false);
      setPropostaSelecionada(null);
      carregarPropostas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar proposta');
    } finally {
      setLoading(false);
    }
  };

  const calcularTempoRestante = (prazo) => {
    if (!prazo) return null;
    const agora = new Date();
    const prazoDate = new Date(prazo);
    const diff = prazoDate - agora;
    
    if (diff <= 0) return { texto: 'Expirada', urgente: true };
    
    const horas = Math.floor(diff / (1000 * 60 * 60));
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return {
      texto: `${horas}h ${minutos}min`,
      urgente: horas < 1,
      progresso: Math.max(0, 100 - (diff / (4 * 60 * 60 * 1000) * 100))
    };
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6" data-testid="propostas-contacto-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <FileCheck className="w-8 h-8 text-blue-600" />
            Propostas de Contacto
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Aprovar contactos a doadores para necessidades críticas
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pendentes" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pendentes
            {propostas.filter(p => p.status === 'Pendente Aprovação').length > 0 && (
              <Badge className="bg-yellow-500 text-white ml-1">
                {propostas.filter(p => p.status === 'Pendente Aprovação').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="todas" className="flex items-center gap-2">
            <FileCheck className="w-4 h-4" />
            Todas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendentes" className="space-y-4">
          {propostas.filter(p => p.status === 'Pendente Aprovação').length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p className="text-lg">Nenhuma proposta pendente de aprovação</p>
                <p className="text-sm">Todas as propostas foram processadas</p>
              </CardContent>
            </Card>
          ) : (
            propostas.filter(p => p.status === 'Pendente Aprovação').map(proposta => {
              const tempoRestante = calcularTempoRestante(proposta.prazo_aprovacao);
              
              return (
                <Card key={proposta.id} className={`border-2 ${tempoRestante?.urgente ? 'border-red-400 bg-red-50' : 'border-yellow-300'}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Header da proposta */}
                        <div className="flex items-center gap-3 mb-4">
                          <Badge className={STATUS_COLORS[proposta.status]}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {proposta.status}
                          </Badge>
                          <span className="text-sm text-slate-500">{proposta.codigo_proposta}</span>
                          
                          {tempoRestante && (
                            <div className={`flex items-center gap-2 ml-auto ${tempoRestante.urgente ? 'text-red-600' : 'text-yellow-600'}`}>
                              <Clock className="w-4 h-4" />
                              <span className="font-medium">{tempoRestante.texto}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Info do Gap */}
                        {proposta.gap && (
                          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-2">
                              <Target className="w-4 h-4 text-red-500" />
                              Necessidade Crítica
                            </h4>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Recurso:</span>
                                <p className="font-medium">{proposta.gap.tipo_recurso_nome || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">Quantidade:</span>
                                <p className="font-medium">{proposta.gap.quantidade_faltante || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">Beneficiários:</span>
                                <p className="font-medium">{proposta.gap.num_beneficiarios_afetados || 'N/A'} pessoas</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Estratégia IA */}
                        <div className="flex items-center gap-4 mb-4">
                          <div className="flex items-center gap-2">
                            <Brain className="w-4 h-4 text-purple-500" />
                            <span className="text-sm text-slate-500">Estratégia IA:</span>
                            <Badge className={ESTRATEGIA_COLORS[proposta.estrategia_ia?.estrategia]}>
                              {proposta.estrategia_ia?.estrategia === 'CONTACTO_DIRETO' ? 'Contacto Direto' : 'Campanha Urgente'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-500" />
                            <span className="text-sm">
                              <strong>{proposta.num_doadores_sugeridos}</strong> doadores identificados
                            </span>
                          </div>
                        </div>
                        
                        {/* Top Doadores Preview */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-slate-500">Top doadores:</span>
                          {proposta.doadores_sugeridos?.slice(0, 3).map((d, idx) => (
                            <Badge key={idx} variant="outline" className="flex items-center gap-1">
                              {d.tipo_doador === 'Empresa' ? 
                                <Building2 className="w-3 h-3" /> : 
                                <User className="w-3 h-3" />
                              }
                              {d.nome}
                              <span className={`ml-1 font-bold ${getScoreColor(d.score_match)}`}>
                                {d.score_match?.toFixed(0)}
                              </span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {/* Botões de ação */}
                      <div className="flex flex-col gap-2 ml-4">
                        <Button 
                          onClick={() => handleAbrirAprovacao(proposta)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Revisar e Aprovar
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/doadores/propostas/${proposta.id}`)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Ver Detalhes
                        </Button>
                      </div>
                    </div>
                    
                    {/* Progress bar do prazo */}
                    {tempoRestante && tempoRestante.progresso !== undefined && (
                      <div className="mt-4">
                        <Progress 
                          value={tempoRestante.progresso} 
                          className={`h-2 ${tempoRestante.urgente ? 'bg-red-200' : 'bg-yellow-200'}`}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Prazo para aprovação: {new Date(proposta.prazo_aprovacao).toLocaleString('pt-PT')}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="todas" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Gap/Necessidade</TableHead>
                    <TableHead className="text-center">Doadores</TableHead>
                    <TableHead>Estratégia</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propostas.map(proposta => (
                    <TableRow key={proposta.id}>
                      <TableCell className="font-mono text-sm">
                        {proposta.codigo_proposta}
                      </TableCell>
                      <TableCell>
                        {proposta.gap?.tipo_recurso_nome || 'N/A'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{proposta.num_doadores_sugeridos}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={ESTRATEGIA_COLORS[proposta.estrategia_ia?.estrategia]} variant="outline">
                          {proposta.estrategia_ia?.estrategia === 'CONTACTO_DIRETO' ? 'Direto' : 'Campanha'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[proposta.status]}>
                          {proposta.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(proposta.created_at).toLocaleDateString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/doadores/propostas/${proposta.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {proposta.status === 'Pendente Aprovação' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleAbrirAprovacao(proposta)}
                            className="text-green-600"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Aprovação */}
      <Dialog open={modalAprovar} onOpenChange={setModalAprovar}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" />
              Revisar Proposta de Contacto
            </DialogTitle>
            <DialogDescription>
              {propostaSelecionada?.codigo_proposta}
            </DialogDescription>
          </DialogHeader>
          
          {propostaSelecionada && (
            <div className="space-y-6">
              {/* Info do Gap */}
              {propostaSelecionada.gap && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 dark:text-red-200 flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5" />
                    NECESSIDADE CRÍTICA
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Recurso:</span>
                      <p className="font-semibold">{propostaSelecionada.gap.tipo_recurso_nome}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Quantidade:</span>
                      <p className="font-semibold">{propostaSelecionada.gap.quantidade_faltante}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Beneficiários:</span>
                      <p className="font-semibold">{propostaSelecionada.gap.num_beneficiarios_afetados} pessoas</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Criticidade:</span>
                      <p className="font-semibold text-red-600">{propostaSelecionada.gap.criticidade}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Estratégia IA */}
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 dark:text-purple-200 flex items-center gap-2 mb-2">
                  <Brain className="w-5 h-5" />
                  RECOMENDAÇÃO IA
                </h4>
                <div className="flex items-center gap-4">
                  <Badge className={`${ESTRATEGIA_COLORS[propostaSelecionada.estrategia_ia?.estrategia]} text-white`}>
                    {propostaSelecionada.estrategia_ia?.estrategia === 'CONTACTO_DIRETO' ? 'Contacto Direto' : 'Campanha Urgente'}
                  </Badge>
                  <p className="text-sm text-slate-600">{propostaSelecionada.estrategia_ia?.justificacao}</p>
                </div>
              </div>
              
              {/* Lista de Doadores */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Doadores Sugeridos ({propostaSelecionada.doadores_sugeridos?.length || 0})
                </h4>
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Doador</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead>Contacto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {propostaSelecionada.doadores_sugeridos?.map((d, idx) => (
                        <TableRow key={d.doador_id}>
                          <TableCell>
                            <span className={`font-bold ${idx < 3 ? 'text-yellow-600' : ''}`}>
                              #{idx + 1}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {d.tipo_doador === 'Empresa' ? 
                                <Building2 className="w-4 h-4 text-blue-500" /> : 
                                <User className="w-4 h-4 text-green-500" />
                              }
                              <span className="font-medium">{d.nome}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{d.tipo_doador}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`text-lg font-bold ${getScoreColor(d.score_match)}`}>
                              {d.score_match?.toFixed(0)}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3 h-3 text-slate-400" />
                              {d.email}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              {/* Preview Emails */}
              {propostaSelecionada.emails_preview?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-green-600" />
                    Pré-visualização Emails
                  </h4>
                  <div className="border rounded-lg p-4 bg-slate-50 max-h-48 overflow-y-auto">
                    <p className="text-sm text-slate-500 mb-2">
                      <strong>Para:</strong> {propostaSelecionada.emails_preview[0]?.doador_nome}
                    </p>
                    <p className="text-sm mb-2">
                      <strong>Assunto:</strong> {propostaSelecionada.emails_preview[0]?.assunto}
                    </p>
                    <div className="text-sm border-t pt-2 mt-2" 
                      dangerouslySetInnerHTML={{ 
                        __html: propostaSelecionada.emails_preview[0]?.corpo_html || propostaSelecionada.emails_preview[0]?.corpo_texto 
                      }} 
                    />
                  </div>
                </div>
              )}
              
              {/* Decisão */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Decisão</h4>
                
                <div className="flex gap-4 mb-4">
                  <Button
                    variant={decisao.acao === 'APROVAR' ? 'default' : 'outline'}
                    onClick={() => setDecisao(p => ({ ...p, acao: 'APROVAR' }))}
                    className={decisao.acao === 'APROVAR' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aprovar
                  </Button>
                  <Button
                    variant={decisao.acao === 'REJEITAR' ? 'destructive' : 'outline'}
                    onClick={() => setDecisao(p => ({ ...p, acao: 'REJEITAR' }))}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeitar
                  </Button>
                </div>
                
                {decisao.acao === 'APROVAR' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="enviarImediatamente"
                        checked={decisao.enviar_imediatamente}
                        onCheckedChange={v => setDecisao(p => ({ ...p, enviar_imediatamente: v }))}
                      />
                      <Label htmlFor="enviarImediatamente" className="cursor-pointer">
                        Enviar emails imediatamente após aprovação
                      </Label>
                    </div>
                    <div>
                      <Label>Observações (opcional)</Label>
                      <Textarea
                        value={decisao.observacoes}
                        onChange={e => setDecisao(p => ({ ...p, observacoes: e.target.value }))}
                        placeholder="Notas sobre a aprovação..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}
                
                {decisao.acao === 'REJEITAR' && (
                  <div>
                    <Label>Motivo da rejeição *</Label>
                    <Textarea
                      value={decisao.motivo_rejeicao}
                      onChange={e => setDecisao(p => ({ ...p, motivo_rejeicao: e.target.value }))}
                      placeholder="Explique o motivo da rejeição..."
                      rows={3}
                      className="border-red-300"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAprovar(false)}>
              Cancelar
            </Button>
            {decisao.acao === 'APROVAR' ? (
              <Button 
                onClick={handleAprovarRejeitar}
                className="bg-green-600 hover:bg-green-700"
                disabled={loading}
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Aprovar e Enviar
              </Button>
            ) : (
              <Button 
                onClick={handleAprovarRejeitar}
                variant="destructive"
                disabled={loading || !decisao.motivo_rejeicao}
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Rejeitar Proposta
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
